import {
  useMutation,
  UseMutationResult,
  useQuery,
} from '@tanstack/react-query';
import { API } from 'api';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { getPushTokenFromLocalStorage } from 'state/localStorage';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import {
  SINGLE_TOPIC_NOTIFICATION_QUERY_KEY,
  SINGLE_TOPIC_CACHE_TIME_LOGGED_IN,
  SINGLE_TOPIC_CACHE_TIME_LOGGED_OUT,
} from 'features/notifications/notifications.constants';
import { useAuthState } from 'state/useAuthState';

type TopicSubscription = {
  id: number;
  topic: number;
  user: number;
  device: string;
  isActive: boolean;
  dateCreated: string;
  dateModified: string;
};

type TopicSubscriptionListAPI = {
  data: TopicSubscription[];
};

type UseTopicSubscriptionsHookReturn = {
  getSubscriptionToTopic: (topicId: number) => TopicSubscription | null;
  subscribeToTopic: UseMutationResult<
    AxiosResponse<unknown>,
    unknown,
    number,
    unknown
  >;
  unsubscribe: UseMutationResult<
    AxiosResponse<unknown>,
    unknown,
    number,
    unknown
  >;
};

export const useTopicSubscriptions = (): UseTopicSubscriptionsHookReturn => {
  const pushToken = getPushTokenFromLocalStorage();

  const { isAuthenticated } = useAuthState();

  // Only logged-in users can have their notifications synced across devices,
  // so we can cache logged out user subscriptions for longer. @linear:ENG-4913
  const subscriptionCacheTtl = isAuthenticated
    ? SINGLE_TOPIC_CACHE_TIME_LOGGED_IN
    : SINGLE_TOPIC_CACHE_TIME_LOGGED_OUT;

  const clearTopicsCache = async (): Promise<void> => {
    await persistedQueryClient.invalidateQueries({
      queryKey: [SINGLE_TOPIC_NOTIFICATION_QUERY_KEY],
    });
  };

  // This is only used for subscriptions to individual incident topics
  const topicSubscriptions = useQuery<TopicSubscriptionListAPI>(
    {
      queryKey: [SINGLE_TOPIC_NOTIFICATION_QUERY_KEY, pushToken],
      queryFn: () =>
        API.get<TopicSubscription[]>('notifications/topics/subscription/', {
          params: { pushToken },
        }),
      staleTime: subscriptionCacheTtl,
      gcTime: subscriptionCacheTtl,
      /**
       * We need to disable this query if there's no push notification.
       * Critical for use case when user denied permissions but enabled them
       * by clicking on the switch notifications button inside any incident.
       */
      enabled: Boolean(pushToken),
    },
    persistedQueryClient,
  );

  const getSubscriptionToTopic = useCallback(
    (topicId: number): TopicSubscription | null => {
      // This is only used for subscriptions to individual incident topics
      const { data } = topicSubscriptions;
      if (!data) return null;

      const topicSubscription = data.data.find(
        (subscription: TopicSubscription) => subscription.topic === topicId,
      );
      if (!topicSubscription) {
        return null;
      }

      return topicSubscription;
    },
    [topicSubscriptions],
  );

  // This is only used for subscriptions to individual incident topics
  const subscribeToTopic = useMutation({
    mutationFn: (topicId: number) => {
      const params = {
        device: { pushToken },
        is_active: true,
        topic: topicId,
      };
      return API.post(`notifications/topics/subscription/?`, params);
    },
    onSettled: clearTopicsCache,
  });

  // This is only used for subscriptions to individual incident topics
  const unsubscribe = useMutation({
    mutationFn: (topicSubscriptionId: number) =>
      API.delete(`notifications/topics/subscription/${topicSubscriptionId}/`),
    onSettled: clearTopicsCache,
  });

  return {
    getSubscriptionToTopic,
    subscribeToTopic,
    unsubscribe,
  };
};
